// @ts-check
// Registro no painel lateral (página view-record do Twenty, doc 10, seções 1.1 e 1.2): cartão resumo em linha, campos,
// a linha "Comunicações de marketing" (2.0.0, como na página), abas (timeline, tarefas, notas, arquivos, conversas, agenda; corpo para nota e tarefa), "Abrir em página inteira",
// voltar quando uma relação foi aberta por cima e fechar com Esc (o contêiner do painel cuida do Esc).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { getRecordLabel } from '../../lib/field-metadata.js';
import { Icon } from '../../ui/icon.js';
import { LightIconButton } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { TabList } from '../../ui/tab-list.js';
import {
  DeletedRecordBanner, RecordFieldsList, RecordHandedOffState, RecordSummaryCard, RecordTabContent, getRecordTabs, useRecordShowData,
  useRestoreRecord,
} from '../record-show/record-show-page.js';
import { closeSidePanel, goBackInRecordPanel } from './side-panel-state.js';
import { hasStageScope } from '../../data/stage-scope.js';
import { openRecord } from './open-record.js';
import { MarketingConsentRow } from '../privacy/marketing-consent-row.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
// seções do módulo Atendimento (3.0.0): somem fora do próprio objeto e com o módulo desligado
import { ServiceTicketRecordSection } from '../service-desk/service-ticket-record-section.js';
import { HouseholdRetentionSection } from '../retention/household-retention-section.js';

/**
 * @param {{ stack: import('../../lib/record-panel.js').RecordPanelEntry[] }} props
 */
export function RecordSidePanel({ stack }) {
  const entry = stack[stack.length - 1];
  return html`<${RecordPanelContent} key=${`${entry.objectNameSingular}:${entry.recordId}`} objectNameSingular=${entry.objectNameSingular}
    recordId=${entry.recordId} canGoBack=${stack.length > 1} />`;
}

/**
 * @param {{ objectNameSingular: string, recordId: string, canGoBack: boolean }} props
 */
function RecordPanelContent({ objectNameSingular, recordId, canGoBack }) {
  const { objectMetadata, record, status, reloadRecord, handedOffStage } = useRecordShowData(objectNameSingular, recordId);
  const tabs = getRecordTabs(objectNameSingular);
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const { isRestoring, restoreRecord } = useRestoreRecord(objectMetadata, recordId, reloadRecord);
  const isDeleted = Boolean(record?.deletedAt);

  // Ctrl/Cmd+Enter no painel abre a página inteira, como o atalho da tabela
  useEffect(() => {
    /** @param {KeyboardEvent} event */
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && /** @type {HTMLElement} */ (event.target).closest?.('.record-side-panel')) {
        event.preventDefault();
        openRecord({ objectNameSingular, recordId, openIn: 'RECORD_PAGE' });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [objectNameSingular, recordId]);

  const objectLabel = objectMetadata ? translateMetadataLabel(objectMetadata.labelSingular) : '';
  return html`<div class="side-panel-page record-side-panel" data-testid="record-side-panel" tabindex="-1">
    <div class="side-panel-top-bar">
      <div class="side-panel-top-bar-left">
        ${canGoBack ? html`<${LightIconButton} icon="IconArrowLeft" accent="tertiary" ariaLabel=${t('recordPage.panel.back')} onClick=${goBackInRecordPanel} />` : null}
        <span class="side-panel-page-chip">
          <${Icon} name=${objectMetadata?.icon ?? 'IconCube'} size=${16} />
          <span>${objectMetadata && record ? getRecordLabel(objectMetadata, record) || t('record.untitled') : objectLabel}</span>
        </span>
      </div>
      <div class="side-panel-top-bar-left record-side-panel-actions">
        <${LightIconButton} icon="IconArrowsDiagonal" accent="tertiary" ariaLabel=${t('recordPage.panel.openFullPage')} title=${t('recordPage.panel.openFullPage')}
          data-testid="record-panel-expand" onClick=${() => openRecord({ objectNameSingular, recordId, openIn: 'RECORD_PAGE' })} />
        <${LightIconButton} icon="IconX" accent="tertiary" ariaLabel=${t('sidePanel.close')} onClick=${closeSidePanel} />
      </div>
    </div>
    <div class="side-panel-content record-side-panel-content">
      ${objectMetadata && status === 'handedOff'
        ? html`<${RecordHandedOffState} objectMetadata=${objectMetadata} recordId=${recordId} stageLabel=${handedOffStage ?? ''}
            panelAction=${canGoBack ? { label: t('recordPage.panel.back'), onClick: goBackInRecordPanel } : { label: t('sidePanel.close'), onClick: closeSidePanel }} />`
        : !objectMetadata || status === 'notFound'
        ? html`<${EmptyState} icon="IconFileOff" title=${t('recordPage.panel.notFound')} subtitle=${hasStageScope() ? t('recordPage.outOfScope') : undefined} />`
        : !record
          ? html`<div class="record-summary-card" data-layout="row"><${Skeleton} width=${40} height=${40} /><${Skeleton} width=${140} height=${16} /></div>`
          : html`
              ${isDeleted
                ? html`<${DeletedRecordBanner} objectMetadata=${objectMetadata} record=${record} isRestoring=${isRestoring} onRestore=${restoreRecord} />`
                : null}
              <${RecordSummaryCard} objectMetadata=${objectMetadata} record=${record} isReadOnly=${isDeleted} layout="row" />
              <${RecordFieldsList} objectMetadata=${objectMetadata} record=${record} isReadOnly=${isDeleted} />
              ${!isDeleted ? html`<${MarketingConsentRow} objectMetadata=${objectMetadata} record=${record} onChanged=${reloadRecord} />` : null}
              ${!isDeleted ? html`<${ServiceTicketRecordSection} objectMetadata=${objectMetadata} record=${record} onChanged=${reloadRecord} />` : null}
              ${!isDeleted ? html`<${HouseholdRetentionSection} objectMetadata=${objectMetadata} record=${record} onChanged=${reloadRecord} />` : null}
              <${TabList} tabs=${tabs} activeTabId=${activeTab} onChange=${setActiveTab} />
              <div class="record-side-panel-tab" data-tab=${activeTab}>
                <${RecordTabContent} objectMetadata=${objectMetadata} record=${record} activeTab=${activeTab} isReadOnly=${isDeleted} />
              </div>
            `}
    </div>
  </div>`;
}
